import { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, X } from "lucide-react";

/**
 * A type-to-filter dropdown over governed master data.
 *
 * The SOW requires design selections to REFERENCE master records rather than repeat their names
 * as free text — a design that says "washed linen" as a string cannot be traced when that
 * material's price, certification or lifecycle status changes. So this always returns the whole
 * option object (carrying its id), never the typed string, and there is no way to submit a value
 * that is not in the list.
 *
 * Options: { id, label, sublabel?, group?, swatch? }. `swatch` is a hex string and renders as a
 * colour dot, which is why the colour picker needs no separate component.
 */
export default function SearchSelect({
  options = [],
  value = null,
  onChange,
  placeholder = "Search…",
  emptyMessage = "Nothing to choose from.",
  grouped = false,
  autoFocus = false,
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Click-outside closes. Without this the list stays open behind the next thing you click.
  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.label, o.sublabel, o.group].filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [options, query]);

  // Preserve the order the caller supplied — for components that is the library's own type order,
  // which reads better than alphabetising the headings.
  const groups = useMemo(() => {
    if (!grouped) return [["", filtered]];
    const out = [];
    for (const o of filtered) {
      const key = o.group || "Other";
      const bucket = out.find(([k]) => k === key);
      if (bucket) bucket[1].push(o);
      else out.push([key, [o]]);
    }
    return out;
  }, [filtered, grouped]);

  const select = (option) => {
    onChange(option);
    setQuery("");
    setOpen(false);
  };

  if (options.length === 0) {
    return (
      <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md px-3 py-2">
        {emptyMessage}
      </p>
    );
  }

  return (
    <div className="relative" ref={boxRef}>
      {value ? (
        <div className="w-full flex items-center gap-2 border border-zinc-200 rounded-md px-3 py-2 text-sm bg-white">
          {value.swatch && (
            <span
              className="w-3 h-3 rounded-full border border-zinc-300 shrink-0"
              style={{ backgroundColor: value.swatch }}
            />
          )}
          <span className="truncate">{value.label}</span>
          {value.sublabel && <span className="text-xs text-zinc-400 truncate">{value.sublabel}</span>}
          <button
            type="button"
            onClick={() => { onChange(null); setOpen(true); }}
            aria-label="Clear selection"
            className="ml-auto text-zinc-400 hover:text-zinc-700 shrink-0"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
          <input
            value={query}
            autoFocus={autoFocus}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setOpen(false);
              if (e.key === "Enter" && filtered.length > 0) { e.preventDefault(); select(filtered[0]); }
            }}
            placeholder={placeholder}
            className="w-full border border-zinc-200 rounded-md pl-8 pr-8 py-2 text-sm"
          />
          <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
        </div>
      )}

      {open && !value && (
        <div className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-zinc-200 rounded-md shadow-lg">
          {filtered.length === 0 ? (
            <p className="px-3 py-2 text-xs text-zinc-400">No match for “{query}”.</p>
          ) : (
            groups.map(([groupLabel, items]) => (
              <div key={groupLabel || "all"}>
                {grouped && groupLabel && (
                  <p className="sticky top-0 px-3 py-1 bg-zinc-50 border-b border-zinc-100 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                    {groupLabel}
                  </p>
                )}
                {items.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => select(o)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50"
                  >
                    {o.swatch && (
                      <span
                        className="w-3 h-3 rounded-full border border-zinc-300 shrink-0"
                        style={{ backgroundColor: o.swatch }}
                      />
                    )}
                    <span className="truncate">{o.label}</span>
                    {o.sublabel && (
                      <span className="ml-auto text-xs text-zinc-400 truncate pl-2">{o.sublabel}</span>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
